'use client';

import { getClassNames } from '@websolutespa/bom-core';
import { useLabel } from '@websolutespa/bom-mixer-hooks';
import React, { FormEvent, Fragment, useState } from 'react';
import { LlmDecodedMessage, PostFeedback } from '../../../types';
import { useLlm } from '../../../useLlm/useLlm';

export type LlmFeedback = {
  threadId: string;
  messageId: string;
  feedbackRating: number;
  feedbackMessage?: string;
};

export type FeedbackType = ('stars' | 'faces' | 'thumb') & string;

export type FeedbackProps = {
  message: LlmDecodedMessage;
  type?: FeedbackType;
};

export const Feedback: React.FC<FeedbackProps> = ({ type, message }) => {
  const label = useLabel();
  const threadId = useLlm(state => state.threadId) || '';
  const messageId = message.messageId || '';
  const { getApi } = useLlm(state => state.actions);
  const theme = useLlm(state => state.theme);
  const [valid, setValid] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<{ message: string }>();
  const feedbackType: FeedbackType = type || (theme.feedback?.type as FeedbackType) || 'stars';
  const classNames = getClassNames('llm__feedback', `llm__feedback--${feedbackType}`);

  /*
  const [feedbackRating, setFeedbackRating] = useState<number>();
  const onRating = async (event: ChangeEvent<HTMLInputElement>) => {
    console.log(event.currentTarget);
    const value = String(event.currentTarget.value);
    const rating = parseInt(value);
    setFeedbackRating(rating);
  };
  */

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      setError(undefined);
      const formData = new FormData(event.currentTarget as HTMLFormElement);
      const data: Partial<LlmFeedback> = { threadId, messageId };
      for (const [key, value] of formData) {
        switch (key) {
          case 'feedbackRating':
            data[key] = parseInt(String(value));
            break;
          default:
            (data as Record<string, string>)[key] = String(value);
        }
      }
      const valid = Boolean(data.feedbackRating);
      // console.log('onSubmit', data.feedbackRating);
      if (valid) {
        const api = getApi();
        const response = await api.postFeedback(data as PostFeedback);
        if (response) {
          // console.warn('Feedback.onSubmit.success', response, data);
          setSubmitted(true);
        } else {
          // console.warn('Feedback.onSubmit.error');
          setError({ message: 'unknown error' });
        }
      }
    } catch (error: any) {
      console.warn('Feedback.onSubmit.error', error);
      setError(error);
    }
  };

  const onChange = async (event: FormEvent<HTMLFormElement>) => {
    const formData = new FormData(event.currentTarget as HTMLFormElement);
    const data: Partial<LlmFeedback> = { threadId, messageId };
    for (const [key, value] of formData) {
      switch (key) {
        case 'feedbackRating':
          data[key] = parseInt(String(value));
          break;
        default:
          (data as Record<string, string>)[key] = String(value);
      }
    }
    const valid = Boolean(data.feedbackRating);
    // console.log('onChange', data.feedbackRating);
    setValid(valid);
  };

  if (!threadId) {
    return;
  }
  if (!messageId) {
    return;
  }
  return (
    <div className={classNames}>
      <div className="llm__row">
        <div className="llm__col --sm-10 --sm-9 --md-8 --lg-7 --xl-6">
          <div className="llm__feedback__group">
            {error ? (
              <div className="llm__feedback__title">{label('llm.feedbackError')}</div>
            ) : submitted ? (
              <div className="llm__feedback__title">{label('llm.feedbackSent')}</div>
            ) : (
              <>
                <div className="llm__feedback__title">{label('llm.feedbackTitle')}</div>
                <form id={`feedback_${messageId}`} className="llm__feedback__form" onSubmit={onSubmit} onChange={onChange}>
                  <fieldset className="llm__feedback__rating">
                    <legend>{label('llm.feedbackRating')}</legend>
                    <div className="llm__feedback__stars">
                      {new Array(5).fill(0).map((_, i) => (
                        <Fragment key={i}>
                          <input
                            type="radio"
                            name="feedbackRating"
                            id={`feedbackRating_${messageId}_${(i + 1)}`}
                            value={i + 1}
                          /* checked={feedbackRating === (i + 1)} */
                          /* onChange={onRating} */
                          />
                          <label htmlFor={`feedbackRating_${messageId}_${(i + 1)}`}><span>${i + 1}</span></label>
                        </Fragment>
                      ))}
                    </div>
                  </fieldset>
                  <fieldset className="llm__feedback__message">
                    <legend>{label('llm.feedbackMessage')}</legend>
                    <input type="text" name="feedbackMessage" id="feedbackMessage" placeholder={label('llm.feedbackMessagePlaceholder')} />
                  </fieldset>
                  <div className="llm__feedback__cta">
                    <button type="submit" disabled={!valid}>{label('llm.feedbackSend')}</button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
